// PublicationsShowcase.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Displays 3-4 most recent published magazine issues from `magazine_issues`
// as a row of magazine covers. Sits below the Edit Showcase section so the
// proof flow goes: editorial articles  →  printed publications.
//
// Empty-state behaviour: if no published issues exist, the section hides
// itself entirely. Truthful, never invents content.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { fetchIssues } from '../../../services/magazineIssuesService.js';
import { supabase } from '../../../lib/supabaseClient.js';
import { PUBLICATIONS_COPY, getPalette } from '../data/inclusionContent.js';
import { imgUrl } from '../../../utils/imgUrl.js';

const GD   = 'var(--font-heading-primary)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

export default function PublicationsShowcase({ darkMode }) {
  const P = getPalette(darkMode);
  const [issues, setIssues] = useState(null); // null=loading, []=empty, [...]=data

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchIssues();
        if (cancelled) return;
        const published = (data || [])
          .filter(i => i.status === 'published')
          .sort((a, b) => {
            const da = new Date(a.published_at || a.created_at || 0);
            const db = new Date(b.published_at || b.created_at || 0);
            return db - da;
          })
          .slice(0, PUBLICATIONS_COPY.maxIssues || 4);

        // Cover fallback: some legacy issues don't have cover_image set.
        // Use the first page's image as a fallback (same logic as the
        // PublicationsPage itself, so visuals match).
        const needsFallback = published.filter(i => !i.cover_image).map(i => i.id);
        if (needsFallback.length > 0) {
          const { data: firstPages } = await supabase
            .from('magazine_issue_pages')
            .select('issue_id, image_url, thumbnail_url')
            .in('issue_id', needsFallback)
            .eq('page_number', 1);
          const coverByIssue = Object.fromEntries(
            (firstPages || []).map(p => [p.issue_id, p.image_url || p.thumbnail_url])
          );
          published.forEach(i => {
            if (!i.cover_image && coverByIssue[i.id]) i.cover_image = coverByIssue[i.id];
          });
        }

        if (!cancelled) setIssues(published);
      } catch {
        if (!cancelled) setIssues([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (issues === null) return null;       // suppress flash while loading
  if (issues.length === 0) return null;   // honest empty-state, hide entirely

  return (
    <section style={{
      background: P.sectionBg,
      padding:    'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
      borderTop:  `1px solid ${P.border2}`,
      transition: 'background 0.3s ease, border-color 0.3s ease',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Eyebrow + title */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(48px, 6vw, 80px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ width: 36, height: 1, background: GOLD }} />
            <span style={{
              fontFamily: NU, fontSize: 9, fontWeight: 600,
              letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD,
            }}>
              {PUBLICATIONS_COPY.eyebrow}
            </span>
            <span style={{ width: 36, height: 1, background: GOLD }} />
          </div>
          <h2 style={{
            fontFamily: GD,
            fontSize:   'clamp(32px, 4.5vw, 56px)',
            fontWeight: 400,
            color:      P.text,
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            margin:     '0 auto 18px',
            maxWidth:   780,
            transition: 'color 0.3s ease',
          }}>
            {PUBLICATIONS_COPY.title}
          </h2>
          <p style={{
            fontFamily: NU, fontSize: 'clamp(14px, 1.4vw, 17px)',
            color: P.body, lineHeight: 1.7,
            margin: '0 auto', maxWidth: 620,
            transition: 'color 0.3s ease',
          }}>
            {PUBLICATIONS_COPY.body}
          </p>

          {/* Scarcity beat, sits below the body as a separate beat in
              italic gold-caps, so it reads as a stated principle, not a
              clause buried in the body. Frames the covers below as
              competition for limited slots. */}
          {PUBLICATIONS_COPY.scarcity && (
            <p style={{
              fontFamily:    GD,
              fontStyle:     'italic',
              fontSize:      'clamp(13px, 1.2vw, 15px)',
              fontWeight:    400,
              color:         GOLD,
              letterSpacing: '0.02em',
              lineHeight:    1.5,
              margin:        '24px auto 0',
              maxWidth:      560,
            }}>
              {PUBLICATIONS_COPY.scarcity}
            </p>
          )}
        </div>

        {/* Issues grid */}
        <div style={{
          display:             'grid',
          gridTemplateColumns: `repeat(${Math.min(issues.length, 4)}, 1fr)`,
          gap:                 'clamp(20px, 2.4vw, 36px)',
        }}>
          {issues.map(issue => (
            <IssueCover key={issue.id} issue={issue} P={P} />
          ))}
        </div>

        {/* CTA out to /publications */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(48px, 6vw, 72px)' }}>
          <a
            href={PUBLICATIONS_COPY.ctaHref}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              fontFamily: NU, fontSize: 11, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {PUBLICATIONS_COPY.cta}
            <span aria-hidden style={{
              width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)',
            }} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Single magazine issue cover card ───────────────────────────────────────

function IssueCover({ issue, P }) {
  const [hovered, setHovered] = useState(false);
  const img    = issue.cover_image || '';
  const title  = issue.title || issue.name || 'Issue';
  const issueLabel = issue.issue_number || issue.issue_label || '';

  // Format the publish date for the small caption strip
  const publishedDate = issue.published_at
    ? new Date(issue.published_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '';

  return (
    <a
      href={`/publications/${issue.slug || issue.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        textDecoration: 'none', color: 'inherit',
        position: 'relative',
        transition: 'transform 0.45s ease',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
      }}
    >
      {/* Magazine cover proportions: A4 portrait, matches the rendered cover
          (same ratio PublicationModule.jsx uses as COVER_RATIO). A 3:4 box here
          previously force-cropped the taller cover image via objectFit:cover,
          cutting off the LWD logo at the top and the title/subtitle at the
          bottom — the cover rendered, but with its own branding invisible. */}
      <div style={{
        position: 'relative',
        aspectRatio: '2480 / 3508',
        overflow: 'hidden',
        background: '#1a1714',
        boxShadow: hovered
          ? '0 24px 60px rgba(22,20,15,0.20), 0 6px 16px rgba(22,20,15,0.10)'
          : '0 6px 24px rgba(22,20,15,0.10)',
        transition: 'box-shadow 0.45s ease',
      }}>
        {img && (
          <img
            src={imgUrl(img, { width: 800 })}
            alt={title}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 1.4s cubic-bezier(0.25,0.46,0.45,0.94)',
            }}
          />
        )}
        {/* Subtle vertical gradient at the bottom for caption contrast */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(22,20,15,0.45), transparent 30%)',
          pointerEvents: 'none',
        }} />
        {/* Issue label, top-left, in gold caps */}
        {issueLabel && (
          <div style={{
            position: 'absolute', top: 14, left: 14,
            fontFamily: NU, fontSize: 8, fontWeight: 600,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD,
          }}>
            Issue {issueLabel}
          </div>
        )}
      </div>

      {/* Caption */}
      <div style={{ padding: '18px 4px 0' }}>
        <div style={{
          fontFamily: GD, fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontWeight: 400, color: P.text, lineHeight: 1.3,
          letterSpacing: '-0.005em',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          transition: 'color 0.3s ease',
        }}>
          {title}
        </div>
        {publishedDate && (
          <div style={{
            fontFamily: NU, fontSize: 10, fontWeight: 500,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: P.subtle, marginTop: 8,
            transition: 'color 0.3s ease',
          }}>
            {publishedDate}
          </div>
        )}
      </div>
    </a>
  );
}
